<i18n
  src="@/locales/components/views/packing_house/production/uniq_final_packing_list/new_package_group.yml"
  lang="yaml"
></i18n>
<i18n src="@/locales/models/package_group.yml" lang="yaml"></i18n>
<i18n src="@/locales/models/package.yml" lang="yaml"></i18n>

<template>
  <div>
    <go-back-link
      :text="$t('labels.go_back_link')"
      :name="routeName('show_container_pallets')"
      :params="{
        packing_house_process_id: this.packing_house_process_id,
        container_id: this.container_id,
      }"
    >
    </go-back-link>

    <container-header
      :packing_house_process="packing_house_process"
      :container_code="container?.container_code"
      :brand_name="container?.exporter_process_brand?.agro_exporter_brand?.name"
    />

    <alert-danger :errors_data="std_err" />

    <div v-if="load_general_data" class="mt-8">
      <div class="panel">
        <div class="panel-header">
          <h3 class="panel-header-title">
            {{ $t("labels.title") }}
          </h3>
        </div>
        <div class="panel-body">
          <div class="space-y-8 divide-y divide-gray-200">
            <div class="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.package_group.pg_code") }}
                </label>
                <div class="mt-1">
                  <input
                    type="text"
                    v-model="pg_code"
                    :class="['input-text', isInvalid('pg_code')]"
                    autofocus
                  />
                </div>
              </div>
              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.package_group.grouped_at") }}
                </label>
                <div class="mt-1">
                  <date-form v-model="grouped_at" />
                </div>
              </div>
            </div>
            <div class="pt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.package_group.thermo_code") }}
                </label>
                <div class="mt-1">
                  <input
                    type="text"
                    v-model="thermo_code"
                    :class="['input-text', isInvalid('thermo_code')]"
                  />
                </div>
              </div>
              <div class="sm:col-span-6">
                <label class="label-form">
                  {{ $t("model.package_group.comment") }}
                </label>
                <div class="mt-1">
                  <textarea
                    v-model="comment"
                    rows="3"
                    class="input-text-area"
                  />
                </div>
              </div>
            </div>
            <div v-if="manage_packages">
              <div v-for="(package_item, idx) in packages" :key="idx">
                <div v-show="!package_item._remove">
                <div
                  class="pt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-8"
                  v-if="this.allow_from_stock && package_item.from_stock"
                >
                  <div class="sm:col-span-8">
                    <div class="text-right">
                      <button
                        @click.prevent="removePackage(package_item)"
                        class="text-red-500"
                      >
                        <i class="fa-solid fa-close fa-lg px-1"></i>
                      </button>
                    </div>
                  </div>
                  <package-from-stock
                    :unassigned_packages="unassigned_packages"
                    :allow_create_package="allow_create_package"
                    :total="total_unassigned_packages"
                    :weeks="weeks_unassigned_packages"
                    :default_week="package_default_week"
                    :unassigned_packages_by_weeks="unassigned_packages_by_weeks"
                    :new_assigned_package_ids="new_assigned_package_ids"
                    :packing_house_process_id="packing_house_process_id"
                    :container_id="container_id"
                    :fetchPackages="fetchUnassignedPackages"
                    @changeToNewPackage="changeToNewPackage(package_item)"
                    @udpateUnassignedPackagesByWeek="(week, packages) => udpateUnassignedPackagesByWeek(week, packages)"
                    v-model="package_item.id"
                    v-model:quantity="package_item.quantity"
                    v-model:week="package_item.week"
                  ></package-from-stock>
                </div>
                <div
                  class="pt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-8"
                  v-else-if="this.allow_create_package"
                >
                  <div class="sm:col-span-8">
                    <div class="text-right">
                      <button
                        @click.prevent="removePackage(package_item)"
                        class="text-red-500"
                      >
                        <i class="fa-solid fa-close fa-lg px-1"></i>
                      </button>
                    </div>
                  </div>
                  <div class="sm:col-span-4">
                    <ProducerOrOrganizationSelect
                      :packing_house_process_id="packing_house_process_id"
                      :container_id="container_id"
                      v-model:producer_id="package_item.producer_id"
                      v-model:organization_id="package_item.organization_id"
                      v-model:crop_field_id="package_item.crop_field_id"
                    />
                  </div>
                  <div class="sm:col-span-2" v-if="exporter_process_crop_sizes.length > 1">
                    <label class="label-form">{{
                      $t("model.package.exporter_process_crop_size")
                    }}</label>
                    <div class="mt-1">
                      <select-form
                        v-if="exporter_process_crop_sizes.length > 1"
                        :options="exporter_process_crop_sizes"
                        v-model="package_item.exporter_process_crop_size_id"
                        label="name"
                      />
                      <div
                        class="content-data"
                        v-else-if="exporter_process_crop_sizes.length == 1"
                      >
                        {{ exporter_process_crop_sizes[0].name }}
                      </div>
                    </div>
                  </div>
                  <div class="sm:col-span-2">
                    <label class="label-form">{{
                      $t("model.package.quantity")
                    }}</label>
                    <div class="mt-1">
                      <input
                        type="text"
                        v-model.number="package_item.quantity"
                        :class="['input-text']"
                      />
                    </div>
                  </div>

                  <package-attrs
                    v-if="load_general_data"
                    :package_item="package_item"
                    :exporter_process_crop_categories="
                      exporter_process_crop_categories
                    "
                    :exporter_process_crop_varieties="
                      exporter_process_crop_varieties
                    "
                    :exporter_process_crop_sizes="exporter_process_crop_sizes"
                    :exporter_process_crop_packages="
                      exporter_process_crop_packages
                    "
                    :exporter_process_brands="exporter_process_brands"
                    v-model:quantity.number="package_item.quantity"
                    v-model:exporter_process_crop_category_id="
                      package_item.exporter_process_crop_category_id
                    "
                    v-model:exporter_process_crop_variety_id="
                      package_item.exporter_process_crop_variety_id
                    "
                    v-model:exporter_process_crop_size_id="
                      package_item.exporter_process_crop_size_id
                    "
                    v-model:exporter_process_crop_package_id="
                      package_item.exporter_process_crop_package_id
                    "
                    v-model:exporter_process_brand_id="
                      package_item.exporter_process_brand_id
                    "
                    :index="idx"
                  ></package-attrs>
                </div>
                </div>
              </div>
              <div class="col-span-6 sm:col-span-3 text-right py-3">
                <button
                  v-if="allow_from_stock && can_assign_other_package"
                  class="border rounded-md px-4 py-1"
                  @click.prevent="addFromStockPackage()"
                >
                  {{ $t("labels.add_assigned_package_link") }}
                </button>
                <span class="space-actions"></span>
                <button
                  v-if="allow_create_package"
                  class="border rounded-md px-4 py-1"
                  @click.prevent="addPackage()"
                >
                  {{ $t("labels.add_package_link") }}
                </button>
                <p class="px-8 py-2 font-bold">{{ $t('model.package_group.total_packages') }}: {{ total_packages }}</p>
              </div>
            </div>
          </div>
          <div class="sm:flex sm:items-start">
            <div class="mt-3 sm:mt-0 sm:ml-4 sm:text-left sm:w-full">
              <div class="py-5">
                <div class="grid gap-x-4 gap-y-6"></div>
              </div>
            </div>
          </div>
        </div>
        <div class="panel-footer">
          <div class="panel-footer-actions">
            <white-button
              @click.prevent="gotToContainer()"
              :label="$t('labels.cancel_button')"
            />
            <span class="space-actions"></span>
            <success-button
              v-if="!proc_save_button"
              @click.prevent="submit()"
              :label="$t('labels.save_link')"
            />
            <success-button-processing v-else :label="$t('labels.save_link')" />
          </div>
        </div>
      </div>
    </div>
    <loading-message v-else />
  </div>
</template>

<script>
import moment from "moment";
import Requester from "@/views/packing_house/requesters/production/uniq_final_packing_list/new_package_group.js";

import PackageAttrs from "@/components/views/packing_house/production/uniq_final_packing_list/show_container/tabs/packages/PackageAttrs.vue";
import ProducerOrOrganizationSelect from "@/components/views/packing_house/production/uniq_final_packing_list/ProducerOrOrganizationSelect.vue";
import PackageFromStock from "@/components/views/packing_house/production/uniq_final_packing_list/PackageFromStock.vue";

import ContainerHeader from "@/components/views/packing_house/production/uniq_final_packing_list/ContainerHeader.vue";

import ManagePackageGroupMixin from "@/components/views/packing_house/production/uniq_final_packing_list/mixins/manage_package_groups.js";

export default {
  name: "NewPackageGroup",
  components: {
    PackageAttrs,
    ProducerOrOrganizationSelect,
    PackageFromStock,
    ContainerHeader,
  },
  mixins: [ManagePackageGroupMixin],
  emit: ["update:packages-groups"],
  props: {
    packing_house_id: { required: true },
    packing_house_process_id: { required: true },
    container_id: { required: true },
  },
  data() {
    return {
      load_general_data: false,
      proc_save_button: false,
      packing_house_process: null,
    };
  },
  computed: {
    route_query: function () {
      return this.$router.currentRoute._value.query;
    },
    query_row: function () {
      if ((this.route_query, this.route_query.row))
        return this.route_query.row;
      return null;
    },
    query_col: function () {
      if ((this.route_query, this.route_query.col))
        return this.route_query.col;
      return null;
    },
    container_code() {
      return this.container ? this.container.container_code : "";
    },
    is_query_position_valid: function () {
      return Boolean(this.query_row) && Boolean(this.query_col);
    },
    position_params: function () {
      if (this.is_query_position_valid)
        return { row_position: this.query_row, col_position: this.query_col };
      return {};
    },
    grouped_at_formatted: function () {
      if (this.grouped_at) return moment(this.grouped_at).format("YYYY-MM-DD");

      return null;
    },
    packages_params: function () {
      return this.packages.filter((pkg) => this.validPackage(pkg));
    },
  },
  methods: {
    loadData: async function () {
      this.fetchPackingHouseProcess();
      await this.fetchContainer();
      if (this.allow_create_package) {
        await this.fetchExporterProcessCropCategories();
        await this.fetchExporterProcessCropVarieties();
        await this.fetchExporterProcessCropSizes();
        await this.fetchExporterProcessCropPackages();
        await this.fetchExporterProcessBrands();
      }
      if (this.allow_from_stock) await this.fetchUnassignedPackages();
      this.load_general_data = true;
      this.addInitialPackage();
    },
    fetchUnassignedPackages: async function (filters = {}) {
      let string_query = '';
      Object.keys(filters).forEach((filter) => {
        string_query += `${filter}=${filters[filter]}`
      });
      let response = await Requester.fetchUnassignedPackages({
        packing_house_process_id: this.packing_house_process_id,
        container_id: this.container_id,
        filters: string_query
      });
      if (string_query) {
        this.unassigned_packages = response.data.packages;
      }
      this.total_unassigned_packages = response.data.total;
      this.weeks_unassigned_packages = response.data.weeks;
      return {
        packages: response.data.packages,
        total: response.data.total,
        weeks: response.data.weeks
      }
    },
    fetchPackingHouseProcess: async function () {
      let response = await Requester.fetchPackingHouseProcess({
        packing_house_process_id: this.packing_house_process_id,
      });
      this.packing_house_process = response.data;
    },
    fetchContainer: async function () {
      let response = await Requester.fetchContainer({
        packing_house_process_id: this.packing_house_process_id,
        container_id: this.container_id,
      });
      this.container = response.data;
      this.grouped_at = this.container.load_date;
    },
    fetchExporterProcessCropCategories: async function () {
      let response = await Requester.fetchExportProcessCropCategories(
        this.packing_house_process_id,
        this.container_id
      );

      this.exporter_process_crop_categories = response.data;
    },
    fetchExporterProcessCropVarieties: async function () {
      let response = await Requester.fetchExportProcessCropVarieties(
        this.packing_house_process_id,
        this.container_id
      );

      this.exporter_process_crop_varieties = response.data;
    },
    fetchExporterProcessBrands: async function () {
      let response = await Requester.fetchExportProcessBrands(
        this.packing_house_process_id,
        this.container_id
      );

      this.exporter_process_brands = response.data;
    },
    fetchExporterProcessCropPackages: async function () {
      let response = await Requester.fetchExportProcessCropPackages(
        this.packing_house_process_id,
        this.container_id
      );

      this.exporter_process_crop_packages = response.data;
    },
    fetchExporterProcessCropSizes: async function () {
      let response = await Requester.fetchExportProcessCropSizes(
        this.packing_house_process_id,
        this.container_id
      );
      this.exporter_process_crop_sizes = response.data;
    },
    gotToContainer: function () {
      this.$router.push({
        name: this.routeName("show_container_pallets"),
        params: {
          packing_house_process_id: this.packing_house_process_id,
          container_id: this.container_id,
        },
      });
    },
    removePackage: function (package_item) {
      package_item._remove = true;
    },
    submit: async function () {
      this.proc_save_button = true;
      try {
        let response = await Requester.createPackageGroup({
          container_id: this.container_id,
          packing_house_process_id: this.packing_house_process_id,
          pg_code: this.pg_code,
          comment: this.comment,
          thermo_code: this.thermo_code,
          grouped_at: this.grouped_at_formatted,
          ...this.position_params,
          packages: this.packages_params,
        });

        this.registerSuccessNotification({
          title: response.data.success_message,
        });

        this.gotToContainer();
      } catch (error) {
        if (error.response.status == 422) {
          this.loadStandardErrorMessages({
            title: error.response.data.error_title,
            messages: error.response.data.error_messages,
            fields: error.response.data.error_fields,
          });
        }
      }
      this.proc_save_button = false;
    },
    hasOneItem(arr) {
      return arr.length === 1 ? arr[0].id : null;
    },
  },
  created: function () {
    this.checkIfAuthenticated().then(() =>
      this.checkIfAuthorized({
        role: this.$route.meta.role,
        permission: this.$route.meta.permission,
        entity: {
          packing_house_id: this.packing_house_id,
        },
      }).then(() => {
        this.loadData();
      })
    );
  },
};
</script>
